import React, { useEffect, useRef } from 'react';

import { GMetrixComboBoxList } from 'components/GMetrixComboBoxList/GMetrixComboBoxList';
import GMetrixDialogConfirm from 'components/GMetrixDialogConfirm/GMetrixDialogConfirm';
import OrderTransactionList from 'components/OrderTransactionList/OrderTransactionList';
import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { LicenseInfoDialog } from 'components/LicenseInfoDialog/LicenseInfoDialog';
import GMetrixDialogShell from 'components/GMetrixDialogShell/GMetrixDialogShell';
import { Alert, Col, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { GroupCodeWrapper } from 'components/GroupCodeWrapper/GroupCodeWrapper';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { getMostRestrictedLicenseDeliverable } from 'utils/objects';
import { type GroupPermissions } from 'pages/groups/types';
import { FormattedMessage, useIntl } from 'react-intl';
import { FullfilledApiResponse } from 'apis/ApiCaller';
import { StudentGroupApi } from 'apis/StudentGroupApi';
import { useGroupDetails } from 'pages/groups/context';
import { ToolTip } from 'components/ToolTip/ToolTip';
import { useNotification } from 'app/notifications';
import { Instructor } from 'apis/InstructorApi';
import { useSuperState } from 'hooks';

interface ClassroomDetailsProps {
    contentTiedToInactiveOrder: boolean;
    permissions: GroupPermissions;
    reloadGroup: () => Promise<void>;
}

interface ClassroomEditForm {
    archived?: boolean;
    availableUses?: number | undefined;
    description?: string | undefined;
    identifier?: string | undefined;
    instructors?: Instructor[] | undefined;
    name?: string | undefined;
}

export function ClassroomDetails({ contentTiedToInactiveOrder, permissions, reloadGroup }: ClassroomDetailsProps) {

    const [state, setState] = useSuperState({
        error: null as string,
        formDetails: null as ClassroomEditForm,
        isLimited: false,
        loading: false,
        originalCode: null as string,
        showEditModal: false
    });
    const { error, formDetails, isLimited, loading, originalCode, showEditModal } = state;

    const { formatMessage } = useIntl();
    const { pageContext, update } = useGroupDetails();
    const { createToast } = useNotification();

    const gmetrixConfirmRef = useRef<any>();
    const gmetrixDialogShellRef = useRef<any>();

    // Compute from tab data instead of making API calls
    const instructors = pageContext?.Data.allInstructors ?? [];

    const leastRedemptions = (() => {

        let min = 999;
        const transactions = pageContext?.Data.transactions;
        if (transactions && transactions.length > 0) {

            transactions.forEach(t => {

                if (t.UsesRemaining && t.UsesRemaining < min && t.UsesRemaining >= 0) {

                    min = t.UsesRemaining;

                }

            });

        }
        return min;

    })();

    const maxUses = (() => {

        const licenses = pageContext?.Data.licenses;
        const content = pageContext?.Data.content;
        if (licenses && content) {

            const restrictedLicense = getMostRestrictedLicenseDeliverable(licenses, content);
            return restrictedLicense.avaliableStudentsAllowed === -1 ? 999 : restrictedLicense.avaliableStudentsAllowed;

        }
        return 0;

    })();

    const clearError = () => setState({ error: null });

    const setFormDetails = (formDetails: ClassroomEditForm) => setState({ formDetails });

    const setArchiveStatusClassroom = async (studentGroup: number, archiveStatus: boolean) => {

        try {

            const result = await StudentGroupApi.SetArchivedStatus(studentGroup, archiveStatus);

            if (!result.Done) {

                createToast(result.Message, 'danger');
                return;

            }

            const message = archiveStatus
                ? formatMessage({ id: 'classroom_was_archived_successfully.46f5d0f4', defaultMessage: 'Classroom was archived successfully' })
                : formatMessage({ id: 'classroom_was_restored.2f8ff279', defaultMessage: 'Classroom was restored' });

            createToast(message, 'success');

            if (pageContext?.Data.groupDetails) {

                update({
                    groupDetails: {
                        ...pageContext.Data.groupDetails,
                        Archived: archiveStatus
                    },
                    groupDetailsOriginal: {
                        ...pageContext.Data.groupDetails,
                        Archived: archiveStatus
                    }
                });

            }

        } catch (e){

            createToast(e?.Message || e, 'danger');

        }

    };

    const onSuccessClick = async () => {

        try {

            let maxStudentCheck = formDetails?.availableUses;
            let studentsLength = pageContext?.Data.groupDetails?.StudentCount;

            if (studentsLength === undefined) studentsLength = 0;

            if (formDetails?.instructors && formDetails?.instructors.length === 0) {

                createToast(formatMessage({ id: 'classrooms_need_at_least_1_instructor.7acd29a1', defaultMessage: 'Classrooms need at least 1 instructor.' }), 'danger');

                return;

            }

            if (maxStudentCheck === undefined) {

                maxStudentCheck = 0;

            } else {

                if (pageContext?.Data.transactionDetails?.IsUnlimited) {

                    if (maxStudentCheck >= studentsLength || maxStudentCheck === 0) {

                        // skip error
                    } else {

                        createToast(formatMessage({ id: 'you_cannot_set_a_max_student_amount_that.804f820a', defaultMessage: 'You cannot set a max student amount that is lower than the number of students already in the Classroom ({count} students).' }, { count: studentsLength }), 'danger');

                        return;

                    }

                } else {

                    if (maxStudentCheck >= studentsLength && maxStudentCheck <= leastRedemptions + studentsLength || !isLimited) {

                        // skip case
                    } else {

                        createToast(formatMessage({ id: 'please_set_max_students_to_less_than_red.0781edc4', defaultMessage: 'Please set Max Students to less than {redemptions} (the number of students currently in this classroom, plus the redemptions remaining) or greater than or equal to {students} (the number of students currently in this classroom).' }, { redemptions: leastRedemptions + studentsLength, students: studentsLength }),
                            'danger');

                        return;

                    }

                }

            }

            setState({ loading: true });

            if (pageContext && pageContext?.Data?.groupDetails) {

                const success: FullfilledApiResponse = await StudentGroupApi.CreateEditStudentGroup(
                    pageContext.Id,
                    formDetails?.name ?? '',
                    formDetails?.description ?? '',
                    maxStudentCheck,
                    pageContext.Data.groupDetails.Active,
                    formDetails?.identifier
                );

                if (!success?.Done) throw new Error(formatMessage({ id: 'failed_to_edit_group.7ee9de96', defaultMessage: 'Failed to edit group' }));

                if (success.Done && pageContext.Data.groupDetails && pageContext.Id) {

                    if (formDetails?.instructors && formDetails?.instructors.length) {

                        const instructorListOfIds = formDetails.instructors.map(instructor => instructor.Id);
                        const removedIds: number[] = [];
                        pageContext.Data.groupDetails.Instructors.map(instructor => {

                            if (!instructorListOfIds.includes(instructor.Id)) {

                                removedIds.push(instructor.Id);

                            }

                        });

                        // remove instructors if any to remove
                        if (removedIds) await StudentGroupApi.RemoveInstructorFromGroup([pageContext.Id], removedIds);

                        await StudentGroupApi.AddEditInstructorByGroup([pageContext.Id], instructorListOfIds, true);

                    }

                    createToast(formatMessage({ id: 'the_classroom_was_edited_successfully.0d78d162', defaultMessage: 'The classroom was edited successfully' }), 'success');

                    update({
                        groupDetails: {
                            ...pageContext.Data.groupDetails,
                            Control: formDetails?.identifier?.split('-')[0] + '-' + originalCode?.split('-')[1],
                            Description: formDetails?.description ?? '',
                            Instructors: formDetails?.instructors ?? [],
                            MaxStudents: formDetails?.availableUses,
                            Name: formDetails?.name ?? ''
                        },
                        groupDetailsOriginal: {
                            ...pageContext.Data.groupDetails,
                            Control: formDetails?.identifier?.split('-')[0] + '-' + originalCode?.split('-')[1],
                            Description: formDetails?.description ?? '',
                            Instructors: formDetails?.instructors ?? [],
                            MaxStudents: formDetails?.availableUses,
                            Name: formDetails?.name ?? ''

                        }
                    });

                }

            }

            setState({ loading: false });

            setState({ showEditModal: false });

            // Re-pull the classroom so the saved changes (name, etc.) are reflected in the
            // page header and details from the server, not just the optimistic local update.
            await reloadGroup();

        } catch (e){

            setState({
                error: e?.message || e,
                loading: false
            });

        }

    };

    const resetModal = () => {

        const details = pageContext?.Data.groupDetails;
        setFormDetails({
            name: details?.Name,
            archived: details?.Archived,
            availableUses: details?.MaxStudents,
            description: details?.Description,
            identifier: details?.Control,
            instructors: details?.Instructors
        });
        setState({ showEditModal: false });
        if (details?.MaxStudents && details?.MaxStudents > 0 && details.MaxStudents !== undefined) {

            setState({ isLimited: true });

        } else {

            setState({ isLimited: false });

        }

    };

    useEffect(() => {

        if (pageContext?.Data.groupDetails?.MaxStudents && pageContext?.Data.groupDetails?.MaxStudents > 0) {

            setState({ isLimited: true });

        }

    }, [pageContext?.Data.content]);

    useEffect(() => {

        const details = pageContext?.Data.groupDetails;
        setFormDetails({
            name: details?.Name,
            archived: details?.Archived,
            availableUses: details?.MaxStudents,
            description: details?.Description,
            identifier: details?.Control?.split('-')[0],
            instructors: details?.Instructors
        });
        setState({ originalCode: details?.Control });

    }, [pageContext?.Data.groupDetails]);

    const verifyInput =
        formDetails?.name?.length === 0 ||
        formDetails?.identifier?.length === 0 ||
        formDetails?.instructors?.length === 0;

    const ClassroomForm = () => (
        <Form key={'classroomForm'} >
            <Form.Group as={Col} className="mt-2" controlId="formClassroomName">
                <GMetrixInput
                    disabled={loading}
                    isInvalid={formDetails?.name?.length === 0}
                    label={formatMessage({ id: 'classroom_name.b4f05ab7', defaultMessage: 'Classroom Name' })}
                    onChange={e => setFormDetails({ ...formDetails, name: e.target.value })}
                    placeholder={formatMessage({ id: 'enter_classroom_name_eg_spreadsheets_101.afe01878', defaultMessage: 'Enter Classroom name (e.g., Spreadsheets 101)' })}
                    type="text"
                    value={formDetails?.name}
                />
            </Form.Group>

            <Form.Group as={Col} className="mt-2" controlId="formIdentifier">
                <GMetrixInput
                    disabled={loading}
                    isInvalid={formDetails?.identifier?.length === 0}
                    label={formatMessage({ id: 'identifier.45eac7f9', defaultMessage: 'Identifier' })}
                    maxLength={8}
                    onChange={e => setFormDetails({ ...formDetails, identifier: e.target.value.replace(/[^a-zA-Z0-9]/g, '') })}
                    placeholder={formatMessage({ id: 'max_8_character_code_letters_and_numbers.a31ff703', defaultMessage: 'Max 8-character code (letters & numbers, e.g., OMC101A1)' })}
                    type="text"
                    value={formDetails?.identifier?.split('-')[0]}
                />
                <Form.Text className="text-muted">
                    <FormattedMessage defaultMessage="Used for organizational purposes, EX. MOS2024" id="used_for_organizational_purposes_ex_mos2.d1b7c962" />
                </Form.Text>
            </Form.Group>

            <Form.Group className="mt-2">

                <Form.Label className="m-0 fw-bold">
                    <FormattedMessage defaultMessage="Number of Students Allowed" id="number_of_students_allowed.41cbfb2c" />
                </Form.Label>
                <ToolTip
                    className="mb-1"
                    content={formatMessage({ id: 'this_setting_controls_the_maximum_number.5ba1f4fe', defaultMessage: 'This setting controls the maximum number of students who can join this Classroom. This license currently has {maxUses} seats in total. No Limit: This Classroom can use as many seats as are available in the license pool. Availability may change as other Classrooms sharing the license enroll students. Limit to: Enrollments for this Classroom will stop at the number you set, even if more seats are available on the license.' }, { maxUses })}
                />
                <div>

                    <GMetrixCheckInput
                        onChange={e => {

                            setFormDetails({ ...formDetails, availableUses: 0 });
                            setState({ isLimited: false });

                        }}
                        checked={!isLimited}
                        disabled={loading}
                        label={formatMessage({ id: 'no_limit.059f752f', defaultMessage: 'No limit' })}
                        subLabel="(up to license total)"
                        type={'radio'}
                    />

                    {/* this margin cancels out the input margin for keeping it in line */}
                    <div className="d-flex justify-content start" style={{ marginTop: '15px' }}>

                        <GMetrixCheckInput
                            label={(
                                <div className="d-flex text-nowrap" >
                                    <FormattedMessage defaultMessage="Limit to" id="limit_to.afeb0f97" />

                                    <GMetrixInput
                                        onChange={e => {

                                            const maxValue: number = parseInt(e.target.value);
                                            if (maxValue > maxUses) {

                                                setFormDetails({ ...formDetails, availableUses: maxUses });

                                            } else if (maxValue === 0) {

                                                setFormDetails({ ...formDetails, availableUses: 1 });

                                            } else {

                                                setFormDetails({ ...formDetails, availableUses: maxValue });

                                            }

                                        }}
                                        className="mx-2"
                                        disabled={loading || !isLimited}
                                        max={maxUses}
                                        min={1}
                                        style={{ marginTop: '-15px', width: 100 }}
                                        type="number"
                                        value={formDetails?.availableUses}
                                    />
                                    students
                                </div>
                            )}
                            onChange={e => {

                                setFormDetails({ ...formDetails, availableUses: 1 });
                                setState({ isLimited: true });

                            }}
                            checked={isLimited}
                            disabled={loading}
                            type={'radio'}
                        />
                    </div>
                </div>
            </Form.Group>

            <Form.Group as={Col} className="mt-2" controlId="formInstructors" md={12}>
                <Form.Label className="fw-bold">
                    <FormattedMessage defaultMessage="Instructors*" id="instructors.92fd6e0a" />
                </Form.Label>
                <ToolTip content={formatMessage({ id: 'instructors_are_users_who_can_manage_cla.1dd0e940', defaultMessage: 'Instructors are users who can manage Classrooms, assign content, edit settings, and manually add students.' })} />

                <GMetrixComboBoxList
                    dataList={instructors}
                    disabled={loading}
                    displayNameProp="FirstName"
                    displayNameSecondaryProp="LastName"
                    feedbackMessage="This Classroom must have at least one instructor."
                    selectedListItems={formDetails?.instructors}
                    setDataList={data => setFormDetails({ ...formDetails, instructors: data })}
                    validType={formDetails?.instructors?.length === 0 ? 'invalid' : undefined}
                />
            </Form.Group>

            <Form.Group className="mb-3 mt-2" controlId="formDescription">
                <GMetrixInput
                    as="textarea"
                    disabled={loading}
                    label={formatMessage({ id: 'description.6317db73', defaultMessage: 'Description' })}
                    onChange={e => setFormDetails({ ...formDetails, description: e.target.value })}
                    rows={4}
                    value={formDetails?.description}
                />
            </Form.Group>
        </Form>
    );

    return (
        <>
            {pageContext && pageContext.Data.groupDetails && (
                <>

                    <Row className="mb-3">

                        <Col xs={12}>
                            <div className="d-flex flex-column flex-sm-row justify-content-between mb-2 gap-3">

                                <div className={'order-1 order-sm-0'}>
                                    <GroupCodeWrapper
                                        archived={pageContext.Data.groupDetails?.Archived}
                                        codeToCopy={pageContext.Data.groupDetails.Control}
                                        label={formatMessage({ id: 'code.cc651d9c', defaultMessage: 'Code' })}
                                    />
                                </div>

                                <div className="d-flex justify-content-end gap-1 align-self-start">
                                    {pageContext.Data.groupDetails.Archived ?
                                        (
                                            <GMetrixButton
                                                onClick={async e => {

                                                    const dontRemindMeRestoreClassroom = sessionStorage.getItem('dontRemindMeRestoreClassroom');

                                                    if (dontRemindMeRestoreClassroom && JSON.parse(dontRemindMeRestoreClassroom)) {

                                                        // just restore the classroom without reminded modal
                                                        setArchiveStatusClassroom(pageContext.Id, false);
                                                        return;

                                                    }

                                                    const [success, dontRemindMe] = await gmetrixConfirmRef.current.confirm(formatMessage({ id: 'restore_classroom.51c67748', defaultMessage: 'Restore Classroom' }),
                                                        <>
                                                            <h6>
                                                                <FormattedMessage defaultMessage="Restoring a Classroom will:" id="restoring_a_classroom_will.c3f04134" />
                                                            </h6>
                                                            <ul>
                                                                <li className="mb-2">
                                                                    <FormattedMessage defaultMessage="Restore students’ access to content assigned through the Classroom" id="restore_students_access_to_content_assig.de3e21a0" />
                                                                </li>
                                                                <li className="mb-2">
                                                                    <FormattedMessage defaultMessage="Allow new student enrollments in this Classroom" id="allow_new_student_enrollments_in_this_cl.1ec2e91d" />
                                                                </li>
                                                                <li>
                                                                    <FormattedMessage defaultMessage="Restore the Classroom in report filters" id="restore_the_classroom_in_report_filters.3f7a7f69" />
                                                                </li>
                                                            </ul>
                                                            <h6>
                                                                <FormattedMessage defaultMessage="Do you wish to continue?" id="do_you_wish_to_continue.b5c0f89c" />
                                                            </h6>

                                                        </>,
                                                        { buttonText: formatMessage({ id: 'restore_classroom.51c67748', defaultMessage: 'Restore Classroom' }), iconClass: 'fa-regular fa-undo', variant: 'primary' },
                                                        { label: formatMessage({ id: 'dont_show_again.17a8f957', defaultMessage: 'Don\'t show again' }) }
                                                    );

                                                    if (success) {

                                                        //restore the classroom
                                                        setArchiveStatusClassroom(pageContext.Id, false);

                                                    }

                                                    // sets boolean value to block modal from appearing during the same session
                                                    if (dontRemindMe) {

                                                        sessionStorage.setItem('dontRemindMeRestoreClassroom', JSON.stringify(true));

                                                    }

                                                }}
                                                iconClass="fa-regular fa-undo"
                                                variant="outline-primary"
                                            >
                                                <FormattedMessage defaultMessage="Restore" id="restore.d528bbad" />
                                            </GMetrixButton>
                                        )
                                        :
                                        (
                                            <GMetrixButton
                                                onClick={async e => {

                                                    const dontRemindMeArchiveClassroom = sessionStorage.getItem('dontRemindMeArchiveClassroom');

                                                    if (dontRemindMeArchiveClassroom && JSON.parse(dontRemindMeArchiveClassroom)) {

                                                        // just archive the classroom without reminded modal
                                                        setArchiveStatusClassroom(pageContext.Id, true);
                                                        return;

                                                    }

                                                    const [success, dontRemindMe] = await gmetrixConfirmRef.current.confirm(formatMessage({ id: 'archive_classroom.0433ca58', defaultMessage: 'Archive Classroom' }),
                                                        <>
                                                            <h6>
                                                                <FormattedMessage defaultMessage="Archiving a Classroom will:" id="archiving_a_classroom_will.349ec633" />
                                                            </h6>
                                                            <ul>
                                                                <li className="mb-2">
                                                                    <FormattedMessage defaultMessage="Remove students' access to content assigned through the Classroom" id="remove_students_access_to_content_assign.cb7e16a6" />
                                                                </li>
                                                                <li className="mb-2">
                                                                    <FormattedMessage defaultMessage="Prevent new student enrollments in this Classroom" id="prevent_new_student_enrollments_in_this.f549e299" />
                                                                </li>
                                                                <li>
                                                                    <FormattedMessage defaultMessage="Hide the Classroom from report filters" id="hide_the_classroom_from_report_filters.21a9831b" />
                                                                </li>
                                                            </ul>
                                                            <h6>
                                                                <FormattedMessage defaultMessage="Do you wish to continue?" id="do_you_wish_to_continue.b5c0f89c" />
                                                            </h6>

                                                        </>,
                                                        { buttonText: formatMessage({ id: 'archive_classroom.0433ca58', defaultMessage: 'Archive Classroom' }), iconClass: 'fa-regular fa-cabinet-filing', variant: 'warning' },
                                                        { label: formatMessage({ id: 'dont_show_again.17a8f957', defaultMessage: 'Don\'t show again' }) }
                                                    );

                                                    if (success) {

                                                        //archive the classroom
                                                        setArchiveStatusClassroom(pageContext.Id, true);

                                                    }

                                                    // sets boolean value to block modal from appearing during the same session
                                                    if (dontRemindMe) {

                                                        sessionStorage.setItem('dontRemindMeArchiveClassroom', JSON.stringify(true));

                                                    }

                                                }}
                                                iconClass="fa-regular fa-cabinet-filing"
                                                variant="outline-primary"
                                            >
                                                <FormattedMessage defaultMessage="Archive" id="archive.61ce1f3a" />
                                            </GMetrixButton>
                                        )
                                    }

                                    <GMetrixButton
                                        className="float-right"
                                        disabled={!permissions?.Edit}
                                        iconClass="fa-pencil fa-solid"
                                        onClick={() => setState({ showEditModal: true })}
                                        variant="primary"
                                    >
                                        <FormattedMessage defaultMessage="Edit" id="edit.00978daf" />
                                    </GMetrixButton>
                                </div>

                            </div>
                        </Col>

                        <Col md={4}>

                            <div>
                                <strong>
                                    <FormattedMessage defaultMessage="Classroom Name" id="classroom_name.b4f05ab7" />
                                </strong>
                                <br />
                                {pageContext.Data.groupDetails.Name !== '' ? pageContext.Data.groupDetails.Name : <FormattedMessage defaultMessage="No Name" id="no_name.c0488f60" />}
                            </div>
                            <div className="mt-2">
                                <strong>
                                    <FormattedMessage defaultMessage="Number of Students Allowed" id="number_of_students_allowed.41cbfb2c" />
                                </strong>

                                <br />
                                {/* this needs to be changed */}
                                {pageContext.Data.groupDetails.MaxStudents === undefined || pageContext.Data.groupDetails.MaxStudents === 0 ? (
                                    <div className="d-flex">
                                        <FormattedMessage defaultMessage="No limit" id="no_limit.059f752f" />
                                        {' '}
                                        <i className="ms-2">
                                            <FormattedMessage defaultMessage="(up to license total)" id="up_to_license_total.ce413048" />
                                        </i>
                                    </div>
                                )
                                    : (
                                        <>
                                            {pageContext.Data.groupDetails.StudentCount}
                                            /
                                            {pageContext.Data.groupDetails.MaxStudents}
                                        </>
                                    )}
                                <OverlayTrigger
                                    overlay={(
                                        <Tooltip>
                                            <FormattedMessage defaultMessage="Instructors are users who can manage Classrooms, assign content, edit settings, and manually add students." id="instructors_are_users_who_can_manage_cla.1dd0e940" />
                                        </Tooltip>
                                    )}
                                    placement="top"
                                >
                                    <GMetrixButton
                                        onClick={() => {

                                            gmetrixDialogShellRef.current.create(formatMessage({ id: 'license_information.a6ad4cdd', defaultMessage: 'License Information' }),
                                                <LicenseInfoDialog groupId={pageContext.Data.groupDetails?.Id} transactionDetails={pageContext.Data.transactionDetails} />,
                                                { buttonProps: { buttonText: formatMessage({ id: 'close.99e01fb5', defaultMessage: 'Close' }), hideCancel: true, variant: 'primary' }, size: 'xl' }
                                            );

                                        }}
                                        className={` ${pageContext.Data.groupDetails.MaxStudents && 'ms-2'} p-0`}
                                        size="sm"
                                        variant="link"
                                    >
                                        <i className="fa-light fa-circle-info me-2"></i>
                                        <FormattedMessage defaultMessage="View Details" id="view_details.fa7bb04a" />
                                    </GMetrixButton>

                                </OverlayTrigger>
                            </div>
                            <div className="mt-2">
                                <strong>
                                    <FormattedMessage defaultMessage="Instructors" id="instructors.24923680" />
                                </strong>
                                <br />
                                {Array.isArray(pageContext.Data.groupDetails.Instructors)
                                    ? pageContext.Data.groupDetails.Instructors.map(i => `${i.FirstName} ${i.LastName}`).join(', ')
                                    : pageContext.Data.groupDetails.InstructorString ?? ''}
                            </div>
                            <div className="mt-2">
                                <strong>
                                    <FormattedMessage defaultMessage="Description" id="description.6317db73" />
                                </strong>
                                <p className="text-muted mb-0">
                                    {pageContext.Data.groupDetails.Description}
                                </p>
                            </div>
                        </Col>

                        <Col md={4}>
                            <div>
                                <strong>
                                    <FormattedMessage defaultMessage="Identifier" id="identifier.45eac7f9" />
                                </strong>
                                <br />
                                {pageContext.Data.groupDetails.Control !== '' ? pageContext.Data.groupDetails.Control : '------'}
                            </div>
                            {pageContext.Data.transactionDetails && pageContext.Data.licenseHistory && pageContext.Data.transactionDetails.OrderIds && (
                                <OrderTransactionList
                                    licenseHistory={pageContext.Data.licenseHistory}
                                    orderIds={pageContext.Data.transactionDetails.OrderIds}
                                />
                            )}
                        </Col>
                    </Row>

                    <GMetrixDialog
                        actionButtonClick={onSuccessClick}
                        actionButtonDisabled={verifyInput}
                        actionButtonText={formatMessage({ id: 'save.d44082ae', defaultMessage: 'Save' })}
                        dialogLabel={formatMessage({ id: 'edit_details.429ca491', defaultMessage: 'Edit Details' })}
                        disabled={loading}
                        onCancel={resetModal}
                        onClose={resetModal}
                        show={showEditModal}
                        size="lg"
                    >
                        <Alert dismissible onClose={clearError} show={!!error} variant={'danger'}>
                            {error}
                        </Alert>
                        {ClassroomForm()}

                    </GMetrixDialog>

                    <GMetrixDialogConfirm ref={gmetrixConfirmRef} />
                    <GMetrixDialogShell ref={gmetrixDialogShellRef} />
                </>
            )}
        </>
    );

}
